import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbJsonLd, faqJsonLd } from '../../components/SEO';
import { DEMO_URL, LITE_SIGNUP_URL } from '../../data/links';
import LiteHero from './LiteHero';

const PAGE_URL = 'https://geofleet.ai/lite';
const CONNECTED_LOGOS = ['/images/brands/shopify.svg', '/images/brands/woocommerce.svg'] as const;

/** Real Lite screens (playground sample data), in the same order as `litePage.dayItems`. */
const DAY_SHOTS = ['/images/lite/create-order.webp', '/images/lite/route-planner.webp', '/images/lite/live-tracking.webp', '/images/lite/cod.webp'];

type IconItem = { icon: string; title: string; text: string };
type DayItem = IconItem & { points: string[] };
type Industry = { label: string; href: string };
type Faq = { question: string; answer: string };
type Guide = { label: string; href: string };

const isStr = (v: unknown) => typeof v === 'string';

function readArray<T>(t: TFunction, key: string, isItem: (o: Record<string, unknown>) => boolean): T[] {
  const raw = t(`litePage.${key}`, { returnObjects: true });
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is T => typeof item === 'object' && item !== null && isItem(item as Record<string, unknown>));
}

const isIconItem = (o: Record<string, unknown>) => isStr(o.icon) && isStr(o.title) && isStr(o.text);

export default function LitePage() {
  const { t } = useTranslation();
  const problems = useMemo(() => readArray<IconItem>(t, 'problems', isIconItem), [t]);
  const setupSteps = useMemo(() => readArray<IconItem>(t, 'setupSteps', isIconItem), [t]);
  const dayItems = useMemo(() => readArray<DayItem>(t, 'dayItems', (o) => isIconItem(o) && Array.isArray(o.points)), [t]);
  const connectedItems = useMemo(() => readArray<IconItem>(t, 'connectedItems', isIconItem), [t]);
  const hubPoints = useMemo(() => readArray<IconItem>(t, 'hubPoints', isIconItem), [t]);
  const industries = useMemo(() => readArray<Industry>(t, 'industries', (o) => isStr(o.label) && isStr(o.href)), [t]);
  const faqs = useMemo(() => readArray<Faq>(t, 'faqs', (o) => isStr(o.question) && isStr(o.answer)), [t]);
  const guides = useMemo(() => readArray<Guide>(t, 'guides', (o) => isStr(o.label) && isStr(o.href)), [t]);

  const appJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Geofleet Lite',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, Android, iOS',
      description: t('litePage.heroLead'),
      url: PAGE_URL,
      offers: [
        {
          '@type': 'Offer',
          name: 'Geofleet Lite',
          price: '49',
          priceCurrency: 'USD',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: '49', priceCurrency: 'USD', billingDuration: 'P1M' },
          description: 'Single-hub delivery management with 3 drivers and 500 orders per month.',
        },
        {
          '@type': 'Offer',
          name: 'Geofleet Lite Plus',
          price: '99',
          priceCurrency: 'USD',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: '99', priceCurrency: 'USD', billingDuration: 'P1M' },
          description: 'Single-hub delivery management with 10 drivers, 1,000 orders, Shopify, WooCommerce and Autopilot suggestions.',
        },
        {
          '@type': 'Offer',
          name: 'Geofleet Lite Autopilot',
          price: '179',
          priceCurrency: 'USD',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: '179', priceCurrency: 'USD', billingDuration: 'P1M' },
          description: 'Single-hub delivery management with 25 drivers, 2,000 orders and Autopilot in full Auto mode.',
        },
      ],
      publisher: { '@type': 'Organization', name: 'Geofleet', url: 'https://geofleet.ai' },
    }),
    [t]
  );

  return (
    <div>
      <SEO
        title={t('litePage.seoTitle')}
        exactDocumentTitle
        description={t('litePage.seoDescription')}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: t('litePage.breadcrumb'), url: PAGE_URL },
          ]),
          appJsonLd,
          faqJsonLd(faqs),
        ]}
      />

      <LiteHero />

      {/* Problem */}
      <section className="border-t border-gray-100 bg-white py-16 lg:py-24" aria-labelledby="lite-problem">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('litePage.problemKicker')}</p>
            <h2 id="lite-problem" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">{t('litePage.problemTitle')}</h2>
          </div>
          <ul className="m-0 mt-12 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {problems.map((p) => (
              <li key={p.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500">
                  <i className={`${p.icon} text-lg`} aria-hidden />
                </span>
                <h3 className="mt-4 font-semibold text-gray-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{p.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Integrations and automation */}
      {connectedItems.length > 0 && (
        <section className="border-y border-slate-100 bg-[#f4f7fb] py-16 lg:py-24" aria-labelledby="lite-connected">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('litePage.connectedKicker')}</p>
              <h2 id="lite-connected" className="mt-2 text-3xl font-bold tracking-tight text-slate-950 lg:text-4xl">{t('litePage.connectedTitle')}</h2>
              <p className="mt-4 leading-relaxed text-slate-600">{t('litePage.connectedIntro')}</p>
            </div>
            <ul className="m-0 mt-12 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-5">
              {connectedItems.map((item, index) => (
                <li key={item.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1769ff]">
                    {CONNECTED_LOGOS[index] ? (
                      <img src={CONNECTED_LOGOS[index]} alt="" width={24} height={24} className="h-6 w-6" />
                    ) : (
                      <i className={`${item.icon} text-xl`} aria-hidden />
                    )}
                  </span>
                  <h3 className="mt-4 font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Setup */}
      <section className="bg-slate-50/70 py-16 lg:py-24" aria-labelledby="lite-setup">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('litePage.setupKicker')}</p>
            <h2 id="lite-setup" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">{t('litePage.setupTitle')}</h2>
            <p className="mt-4 leading-relaxed text-gray-600">{t('litePage.setupIntro')}</p>
          </div>
          <ol className="m-0 mt-12 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-5">
            {setupSteps.map((s, i) => (
              <li key={s.title} className="relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <span className="text-xs font-bold text-blue-600">{String(i + 1).padStart(2, '0')}</span>
                <span className="mt-3 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <i className={`${s.icon} text-lg`} aria-hidden />
                </span>
                <h3 className="mt-3 font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* A day with Lite */}
      <section id="lite-day" className="scroll-mt-24 bg-white py-16 lg:py-24" aria-labelledby="lite-day-title">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('litePage.dayKicker')}</p>
            <h2 id="lite-day-title" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">{t('litePage.dayTitle')}</h2>
          </div>
          <ol className="m-0 mt-14 list-none space-y-20 p-0 lg:mt-20 lg:space-y-28">
            {dayItems.map((d, i) => (
              <li key={d.title} className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
                <div className={`lg:col-span-4 ${i % 2 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/25">
                      <i className={`${d.icon} text-xl`} aria-hidden />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="mt-5 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">{d.title}</h3>
                  <p className="mt-3 leading-relaxed text-gray-600">{d.text}</p>
                  <ul className="m-0 mt-5 list-none space-y-2 p-0">
                    {d.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <i className="ri-checkbox-circle-fill text-emerald-500" aria-hidden />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
                {DAY_SHOTS[i] && (
                  <figure className={`m-0 lg:col-span-8 ${i % 2 ? 'lg:order-1' : ''}`}>
                    <div className="rounded-2xl border border-blue-100 bg-blue-50 p-2 sm:p-3">
                      <img
                        src={DAY_SHOTS[i]}
                        alt={t('litePage.screenshotOf', { title: d.title })}
                        width={1920}
                        height={1200}
                        loading="lazy"
                        decoding="async"
                        className="block h-auto w-full rounded-xl border border-gray-200/80 shadow-xl shadow-slate-900/10"
                      />
                    </div>
                  </figure>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Built for one hub */}
      <section className="bg-[#071421] py-16 text-white lg:py-24" aria-labelledby="lite-hub">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-400">{t('litePage.hubKicker')}</p>
            <h2 id="lite-hub" className="mt-2 text-3xl font-bold tracking-tight lg:text-4xl">{t('litePage.hubTitle')}</h2>
          </div>
          <ul className="m-0 mt-12 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4">
            {hubPoints.map((h) => (
              <li key={h.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <i className={`${h.icon} text-2xl text-blue-400`} aria-hidden />
                <h3 className="mt-4 font-semibold">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{h.text}</p>
              </li>
            ))}
          </ul>
          {industries.length > 0 && (
            <div className="mt-12 flex flex-wrap items-center gap-2.5">
              <span className="me-2 text-sm font-semibold text-gray-400">{t('litePage.industriesTitle')}</span>
              {industries.map((ind) => (
                <Link key={ind.href} to={ind.href} className="rounded-full border border-white/15 px-3.5 py-1.5 text-sm text-gray-200 transition-colors hover:border-blue-400 hover:text-white">
                  {ind.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Dedicated pricing page keeps the product page focused on the Lite experience. */}
      <section className="bg-white py-16 lg:py-24" aria-labelledby="lite-pricing">
        <div className="mx-auto grid max-w-6xl items-center gap-8 rounded-[28px] border border-slate-200 bg-gradient-to-br from-white via-white to-blue-50/80 p-7 shadow-[0_18px_55px_rgba(15,23,42,0.06)] sm:p-10 lg:grid-cols-[1fr_auto] lg:p-12">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('litePage.pricingKicker')}</p>
            <h2 id="lite-pricing" className="mt-2 text-3xl font-bold tracking-tight text-slate-950 lg:text-4xl">{t('litePage.pricingTitle')}</h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-slate-600">{t('litePage.pricingIntro')}</p>
            <p className="mt-5 text-sm text-slate-500">{t('litePage.pricingBoundaryText')}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
            <Link to="/pricing/lite" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1769ff] px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/15 transition-all hover:-translate-y-0.5 hover:bg-blue-700">
              {t('litePage.compareCta')}<i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
              {t('pricing.platform.eyebrow')}<i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-16 lg:py-24" aria-labelledby="lite-faq">
        <div className="mx-auto max-w-3xl px-6">
          <h2 id="lite-faq" className="text-center text-3xl font-bold tracking-tight text-gray-900">{t('litePage.faqTitle')}</h2>
          <div className="mt-10 space-y-3">
            {faqs.map((f) => (
              <details key={f.question} className="group rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-gray-900">
                  {f.question}
                  <i className="ri-add-line text-xl text-blue-600 transition-transform group-open:rotate-45" aria-hidden />
                </summary>
                <p className="mt-3 leading-relaxed text-gray-600">{f.answer}</p>
              </details>
            ))}
          </div>
          {guides.length > 0 && (
            <nav className="mt-12" aria-labelledby="lite-guides">
              <h3 id="lite-guides" className="text-sm font-bold uppercase tracking-wider text-gray-500">{t('litePage.guidesTitle')}</h3>
              <ul className="m-0 mt-4 grid list-none gap-3 p-0 sm:grid-cols-2">
                {guides.map((g) => (
                  <li key={g.href}>
                    <Link
                      to={g.href}
                      className="group flex h-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-gray-900 transition-colors hover:border-blue-300 hover:text-blue-700"
                    >
                      {g.label}
                      <i className="ri-arrow-right-line text-blue-600 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-[#071421] px-8 py-14 text-center text-white shadow-xl shadow-slate-950/15">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#1769ff]" aria-hidden />
            <h2 className="relative text-3xl font-bold tracking-tight lg:text-4xl">{t('litePage.ctaTitle')}</h2>
            <p className="relative mx-auto mt-4 max-w-xl text-blue-100">{t('litePage.ctaText')}</p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={LITE_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-blue-700 shadow-lg transition-all hover:-translate-y-0.5"
              >
                {t('litePage.ctaPrimary')}
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </a>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                {t('litePage.ctaTalk')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
